import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Pagination,
  Stack,
  Select,
  Paper,
  Avatar,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaBriefcase,
  FaFilter,
  FaBuilding,
  FaList,
  FaTh,
  FaHourglassHalf,
  FaQuestionCircle,
  FaWifi,
  FaClock,
  FaSlidersH,
  FaExchangeAlt,
  FaCode,
  FaGraduationCap,
} from "react-icons/fa";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../../../Common/Loader/loader";
import { Timer } from "@mui/icons-material";
import { FiFileText, FiHelpCircle } from "react-icons/fi";

const BRAND = "#2d6d96";
const GRADIENT = "linear-gradient(135deg, #2d6d96, #4fa3d1)";
const GRADIENT_HOVER = "linear-gradient(135deg, #245777, #3e90bd)";
const ITEMS_PER_PAGE = 6;

const baseUrl = process.env.REACT_APP_API_PATH;

const JobsList = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [search, setSearch] = useState("");
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobType, setJobType] = useState("");
  const [location, setLocation] = useState("");
  const [skills, setSkills] = useState([]);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("list"); // "list" or "grid"
  const navigate = useNavigate();
  // const [expandedIndex, setExpandedIndex] = useState(null);
  const [locationsList, setLocationsList] = useState([]);

  const getJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/ebench/jobs`);
      const data = response.data;
      //   console.log("Jobs Data:", data.data.body);
      setJobList(data.data.body || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobList([]);
    } finally {
      setLoading(false);
    }
  };

  const getStates = async (country) => {
    try {
      const response = await axios.post(
        "https://countriesnow.space/api/v0.1/countries/states",
        { country }
      );
      return response.data?.data?.states || [];
    } catch (error) {
      console.error("Error fetching states:", error);
      return [];
    }
  };

  useEffect(() => {
    getJobs();
    getStates("India").then((states) => {
      setLocationsList(states.map((s) => s.name) || []);
    });
  }, []);

  const handleApply = (jobId) => {
    if (isLoggedIn) {
      navigate(`/ebench/jobs-details/${jobId}`);
    } else {
      navigate("/login", {
        state: { redirectTo: `/ebench/jobs-details/${jobId}` },
      });
    }
  };

  const allSkills = useMemo(
    () => [...new Set(jobList.flatMap((j) => j.skills || []))],
    [jobList]
  );

  const filteredJobs = useMemo(() => {
    return jobList.filter((job) => {
      const matchesSearch =
        job.jobName?.toLowerCase().includes(search.toLowerCase()) ||
        job.skills?.some((s) => s.toLowerCase().includes(search.toLowerCase()));

      const matchesType = jobType ? job.jobType === jobType : true;
      const matchesLocation = location ? job.state === location : true;
      const matchesSkills = skills.length
        ? skills.some((s) => job.skills?.includes(s))
        : true;

      return matchesSearch && matchesType && matchesLocation && matchesSkills;
    });
  }, [search, jobType, location, skills, jobList]);

  const paginatedJobs = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredJobs.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredJobs, page]);

  const totalPages = Math.ceil(filteredJobs.length / ITEMS_PER_PAGE);

  const getWorkTypeIcon = (workType) => {
    switch (workType) {
      case "ONSITE":
        return <FaBuilding size={14} color={BRAND} />;
      case "REMOTE":
        return <FaWifi size={14} color={BRAND} />;
      case "HYBRID":
        return <FaExchangeAlt size={14} color={BRAND} />;
      default:
        return <FaBriefcase size={14} color={BRAND} />;
    }
  };

  const getJobTypeIcon = (jobType) => {
    switch (jobType) {
      case "PERMANENT":
        return <FaBriefcase size={14} color={BRAND} />;
      case "PART_TIME":
        return <Timer size={14} color={BRAND} />;
      case "INTERNSHIP":
        return <FaGraduationCap size={14} color={BRAND} />;
      case "FREELANCE":
        return <FaCode size={14} color={BRAND} />;
      case "CONTRACT":
        return <FiFileText size={14} color={BRAND} />;
      default:
        return <FiHelpCircle size={14} color={BRAND} />;
    }
  };

  const getEmployementTypeIcon = (employmentType) => {
    switch (employmentType) {
      case "FULL_TIME":
        return <FaBriefcase size={14} color={BRAND} />;
      case "PART_TIME":
        return <FaHourglassHalf size={14} color={BRAND} />;
      case "FLEXIBLE":
        return <FaSlidersH size={14} color={BRAND} />;
      default:
        return <FaQuestionCircle size={14} color={BRAND} />;
    }
  };

  const getJobType = (type) => {
    const map = {
      PERMANENT: "Permanent",
      PART_TIME: "Part Time",
      INTERNSHIP: "Internship",
      FREELANCE: "Freelance",
      CONTRACT: "Contract",
    };
    return map[type] || "N/A";
  };

  const getWorkType = (type) => {
    const map = {
      ONSITE: "Onsite",
      REMOTE: "Remote",
      HYBRID: "Hybrid",
    };
    return map[type] || type || "N/A";
  };

  const getEmploymentType = (type) => {
    const map = {
      FULL_TIME: "Full Time",
      PART_TIME: "Part Time",
      FLEXIBLE: "Flexible",
    };
    return map[type] || type || "N/A";
  };

  const clean = (v) =>
    typeof v === "string" && v.trim().length > 0 ? v.trim() : null;

  const getLocation = (job) => {
    if (job?.workType === "ONSITE" || job?.workType === "HYBRID") {
      const location = [job?.city, job?.state, job?.country]
        .map(clean)
        .filter(Boolean)
        .join(", ");

      return location || "Not Disclosed";
    }

    return clean(job?.location) || "Not Disclosed";
  };

  // List View Component
  const JobListItem = ({ job }) => {
    // const [expanded, setExpanded] = useState(false);

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(45,109,150,0.15)",
            borderColor: BRAND,
            transform: "translateX(4px)",
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/ebench/jobs-details/${job.jobId}`);
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Box display="flex" gap={2} flex={1}>
            {/* Job Avatar */}
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "rgba(45,109,150,0.1)",
                color: BRAND,
                fontWeight: 700,
                fontSize: "1.25rem",
              }}
            >
              {job.jobName?.charAt(0)}
            </Avatar>

            {/* Job Details */}
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                {job.jobName}
              </Typography>
              {/* <Typography
              variant="subtitle1"
              color={BRAND}
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              {job.companyName}
            </Typography> */}

              <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getWorkTypeIcon(job.workType)}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {getWorkType(job.workType)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <FaMapMarkerAlt color="#2d6d96" size={14} />
                  <Typography variant="body2" color="text.secondary">
                    {getLocation(job)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={0.5}>
                  <FaBriefcase color="#2d6d96" size={14} />
                  <Typography variant="body2" color="text.secondary">
                    {job.experience || "N/A"}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <FaClock color="#2d6d96" size={14} />
                  <Typography variant="body2" color="text.secondary">
                    {getEmploymentType(job.employmentType)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {getJobTypeIcon(job.jobType)}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {getJobType(job.jobType)}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, lineHeight: 1.6 }}
              >
                <p
                  className="eBench-job-description mb-3"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      job?.jobDescription
                        ? `${job.jobDescription.slice(0, 250)}${
                            job.jobDescription.length > 250 ? "..." : ""
                          }`
                        : "N/A"
                    ),
                  }}
                ></p>
              </Typography>

              {/* {job.jobDescription.length > 200 && (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  sx={{ textTransform: "none", p: 0, mb: 2, color: BRAND }}
                >
                  {expanded ? "Hide" : "Show more"}
                </Button>
              )} */}

              <Box display="flex" gap={1} flexWrap="wrap">
                {job.skills?.slice(0, 5).map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(45,109,150,0.1)",
                      color: BRAND,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                ))}
                {job.skills?.length > 5 && (
                  <Chip
                    label={`+${job.skills.length - 5} more`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(45,109,150,0.1)",
                      color: BRAND,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Apply Button */}
          <Button
            variant="contained"
            sx={{
              ml: 2,
              mt: { xs: 2, md: 0 },
              color: "#fff",
              fontWeight: 700,
              borderRadius: 2.5,
              textTransform: "none",
              px: 3,
              py: 1,
              background: GRADIENT,
              boxShadow: "0 4px 12px rgba(45,109,150,0.25)",
              "&:hover": {
                background: GRADIENT_HOVER,
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(45,109,150,0.35)",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleApply(job.jobId);
            }}
          >
            Apply Now
          </Button>
        </Box>
      </Paper>
    );
  };

  // Grid View Component
  const JobGridItem = ({ job }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        transition: "all 0.3s ease",
        border: "1px solid rgba(0,0,0,0.08)",
        "&:hover": {
          transform: "translateY(-8px)",
          cursor: "pointer",
          boxShadow: "0 20px 40px rgba(45,109,150,0.25)",
          borderColor: BRAND,
        },
      }}
      onClick={() => navigate(`/ebench/jobs-details/${job.jobId}`)}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "rgba(45,109,150,0.1)",
              color: BRAND,
              fontWeight: 700,
            }}
          >
            {job.jobName?.charAt(0) || "J"}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#1a1a1a" }}>
              {job.jobName}
            </Typography>
            {/* <Typography variant="subtitle2" color={BRAND} fontWeight={600}>
              {job.companyName}
            </Typography> */}
          </Box>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap" mb={2} alignItems="center">
          <Typography
            variant="body2"
            color="text.secondary"
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            {getWorkTypeIcon(job.workType)}
            {getWorkType(job.workType)}
          </Typography>

          <Typography
            variant="body2"
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ fontWeight: 500 }}
          >
            {getJobTypeIcon(job.jobType)}
            {getJobType(job.jobType)}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <FaMapMarkerAlt color="#2d6d96" />
            {getLocation(job)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            display="flex"
            alignItems="center"
            gap={0.5}
          >
            <FaBriefcase color="#2d6d96" />
            {job.experience || "N/A"}
          </Typography>

          <Typography
            variant="body2"
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              fontWeight: 500,
            }}
          >
            {getEmployementTypeIcon(job.employmentType)}

            {getEmploymentType(job.employmentType)}
          </Typography>
        </Box>

        <>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              lineHeight: 1.6,
            }}
          >
            <p
              className="eBench-job-description mb-3"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  job?.jobDescription
                    ? `${job.jobDescription.slice(0, 100)}${
                        job.jobDescription.length > 100 ? "..." : ""
                      }`
                    : "N/A"
                ),
              }}
            ></p>
          </Typography>
          {/* {} */}

          {/*{job.jobDescription.length > 100 && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedIndex(expandedIndex === index ? null : index);
              }}
              sx={{
                textTransform: "none",
                p: 0,
                mb: 2,
                color: BRAND,
              }}
            >
              {expandedIndex === index ? "Hide" : "Show more"}
            </Button>
          )}
            */}
        </>

        <Box display="flex" gap={1} flexWrap="wrap" mb={3}>
          {job.skills?.slice(0, 4).map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              sx={{
                backgroundColor: "rgba(45,109,150,0.12)",
                color: BRAND,
                fontWeight: 600,
              }}
            />
          ))}
          {job.skills?.length > 4 && (
            <Chip
              label={`+${job.skills.length - 4}`}
              size="small"
              sx={{
                backgroundColor: "rgba(45,109,150,0.12)",
                color: BRAND,
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            sx={{
              color: "#fff",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              px: 3,
              background: GRADIENT,
              "&:hover": { background: GRADIENT_HOVER },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleApply(job.jobId);
            }}
          >
            Apply
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Navbar />
      <Box
        sx={{
          minHeight: "100vh",
          py: 6,
          px: { xs: 2, md: 6 },
          background:
            "linear-gradient(180deg, rgba(45,109,150,0.08), #fff 65%)",
        }}
      >
        {/* Header */}
        <Box textAlign="center" mb={5}>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              color: BRAND,
              mb: 1,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Find Your Next Job
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            fontWeight={400}
            sx={{ mb: 2 }}
          >
            Search, filter, and apply to opportunities that match your skills
            and goals
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 720,
              mx: "auto",
              mb: 3,
              lineHeight: 1.7,
            }}
          >
            Explore verified openings from top companies across industries.
            Whether you’re looking for onsite, remote, or hybrid roles, we make
            job hunting simple, fast, and transparent.
          </Typography>

          {/* Quick Stats / Trust Signals */}
          <Box
            display="flex"
            justifyContent="center"
            gap={{ xs: 2, md: 4 }}
            flexWrap="wrap"
          >
            <Typography variant="body2" fontWeight={600} color={BRAND}>
              🔍 1,000+ Active Jobs
            </Typography>
            <Typography variant="body2" fontWeight={600} color={BRAND}>
              🏢 300+ Companies
            </Typography>
            <Typography variant="body2" fontWeight={600} color={BRAND}>
              ⚡ Quick Apply
            </Typography>
          </Box>
        </Box>

        {/* Filters */}
        <Card
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 4,
            boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Job title, skills"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      <FaSearch color={BRAND} />
                    </Box>
                  ),
                }}
                sx={{ "& .MuiInputBase-root": { height: 44 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2} >
              <TextField
                select
                fullWidth
                value={jobType}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected === "" ? (
                      <Typography color="text.secondary">Job Type</Typography>
                    ) : (
                      selected
                    ),
                }}
                onChange={(e) => {
                  setJobType(e.target.value);
                  setPage(1);
                }}
                sx={{ "& .MuiInputBase-root": { height: 44 } }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="FULL_TIME">Full Time</MenuItem>
                <MenuItem value="REMOTE">Remote</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="PART_TIME">Part Time</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                value={location}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected === "" ? (
                      <Typography color="text.secondary">Location</Typography>
                    ) : (
                      selected
                    ),
                }}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setPage(1);
                }}
                sx={{ "& .MuiInputBase-root": { height: 44 } }}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locationsList.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Select
                multiple
                fullWidth
                displayEmpty
                value={skills}
                onChange={(e) => {
                  setSkills(e.target.value);
                  setPage(1);
                }}
                sx={{ height: 44 }}
                endAdornment={
                  skills.length > 0 && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSkills([]);
                          setPage(1);
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
                renderValue={(selected) =>
                  selected.length === 0 ? (
                    <Typography color="text.secondary">
                      Select skills
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {selected.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(45,109,150,0.12)",
                            color: BRAND,
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </Box>
                  )
                }
              >
                {allSkills.map((skill) => (
                  <MenuItem key={skill} value={skill}>
                    {skill}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                startIcon={<FaFilter />}
                onClick={() => {
                  setSearch("");
                  setJobType("");
                  setLocation("");
                  setSkills([]);
                  setPage(1);
                }}
                sx={{
                  height: 44,
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 3,
                  background: GRADIENT,
                  boxShadow: "0 6px 14px rgba(45,109,150,0.35)",
                  "&:hover": { background: GRADIENT_HOVER },
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>

        {!loading && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
            mb={3}
          >
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              {filteredJobs.length} {filteredJobs.length === 1 ? "job" : "jobs"}{" "}
              found
            </Typography>

            <Box display="flex" gap={1}>
              <Button
                variant={viewMode === "list" ? "contained" : "outlined"}
                startIcon={<FaList />}
                onClick={() => setViewMode("list")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  ...(viewMode === "list" && {
                    background: GRADIENT,
                    color: "#fff",
                    "&:hover": { background: GRADIENT_HOVER },
                  }),
                  ...(viewMode !== "list" && {
                    borderColor: BRAND,
                    color: BRAND,
                    "&:hover": { backgroundColor: "rgba(45,109,150,0.05)" },
                  }),
                }}
              >
                List View
              </Button>
              <Button
                variant={viewMode === "grid" ? "contained" : "outlined"}
                startIcon={<FaTh />}
                onClick={() => setViewMode("grid")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  ...(viewMode === "grid" && {
                    background: GRADIENT,
                    color: "#fff",
                    "&:hover": { background: GRADIENT_HOVER },
                  }),
                  ...(viewMode !== "grid" && {
                    borderColor: BRAND,
                    color: BRAND,
                    "&:hover": { backgroundColor: "rgba(45,109,150,0.05)" },
                  }),
                }}
              >
                Grid View
              </Button>
            </Box>
          </Box>
        )}

        {/* Loading  */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            {/* <CircularProgress size={60} sx={{ color: BRAND }} /> */}
            <Loader />
          </Box>
        ) : paginatedJobs.length === 0 ? (
          <Box textAlign="center" py={10}>
            <Typography variant="h5" color="text.secondary" fontWeight={600}>
              No jobs found
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Try adjusting your filters or search criteria
            </Typography>
          </Box>
        ) : viewMode === "list" ? (
          /* List View */
          <Box>
            {paginatedJobs.map((job, index) => (
              <JobListItem index={index} key={job.jobId} job={job} />
            ))}
          </Box>
        ) : (
          /* Grid View */
          <Grid container spacing={4}>
            {paginatedJobs.map((job, index) => (
              <Grid item xs={12} md={6} lg={4} key={job.jobId}>
                <JobGridItem index={index} job={job} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Stack alignItems="center" mt={6}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              shape="rounded"
              size="large"
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: "600 !important",
                  color: "#2d6d96 !important",
                  borderColor: "#2d6d96 !important",
                },
                "& .MuiPaginationItem-root:hover": {
                  backgroundColor: "rgba(45, 109, 150, 0.08) !important",
                },
                "& .Mui-selected": {
                  backgroundColor: "#2d6d96 !important",
                  color: "#fff !important",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "#2d6d96 !important",
                },
              }}
            />
          </Stack>
        )}
      </Box>

      <Footer />
    </>
  );
};

export default JobsList;
